import { Chip, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import {
  CheckCircle,
  MinusCircle,
  MoveUpRight,
  PackageCheck,
  Quote,
  ShoppingBag,
  XCircle,
} from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { Link, useParams } from "react-router-dom";
import { Product as RTKProduct } from "randmar-api-client";
import { ProductImage } from "randmarcomps";
import Path from "../../routes/Path";
import { Product } from "../../stores/product/Product";
import { convertLegacyProductToRTK } from "../../utils/converters";
import CurrencyUtils from "../util/CurrencyUtils";

// Type guard to check if the product is the legacy MobX type
function isLegacyProduct(product: Product | RTKProduct): product is Product {
    // A simple way to check is to see if a property exists on the legacy model that doesn't on the new one.
    // `source` is a good candidate. Or we can check the instance.
    return product instanceof Product || "source" in product;
}

// Props definition allowing either legacy or new product type
interface IProps {
  product: Product | RTKProduct;
}

const ProductGeneral: React.FC<IProps> = ({ product: productFromProps }) => {
  const { applicationId } = useParams<{ applicationId: string }>();

  // Convert legacy product to new RTK type if necessary
  const product = isLegacyProduct(productFromProps)
    ? convertLegacyProductToRTK(productFromProps)
    : productFromProps;

  const handleStateIcon = (state?: string | null) => {
    if (state === "Active") return <CheckCircle color="success" />;
    if (state === "Discontinued") return <XCircle color="error" />;
    if (state === "Hold") return <MinusCircle color="error" />;
    return <Quote color="info" />;
  };

  return (
    <>
      <div className="m-6 flex flex-col items-center justify-between gap-6 lg:flex-row">
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <Typography variant="h2" align="center" paddingTop={{ xs: 1, md: 0 }} paddingBottom={{ xs: 1.5 }}>{product.Title}</Typography>

          <ProductImage randmarSKU={product.RandmarSKU || ""} />

          <div className="flex flex-row items-center justify-center gap-2">
            <Chip
              icon={handleStateIcon(product.State)}
              variant="outlined"
              size="small"
              color={product.State === "Active" ? "success" : "error"}
              title={product.State ?? ""}
              label={product.State}
            />
            <Chip
              icon={<PackageCheck color={product.ManufacturerCategory?.QualificationRequired ? "success" : "inherit"} />}
              variant="outlined"
              size="small"
              color={product.ManufacturerCategory?.QualificationRequired ? "success" : "default"}
              title="Qualification"
              label={
                product.ManufacturerCategory?.QualificationRequired
                  ? "Qualification Required"
                  : "No Qualification Required"
              }
            />
          </div>
          <div className="flex flex-row items-center justify-center gap-2">
            <Chip
              icon={<ShoppingBag color={(product.BodyHTML && product.Title && product.MAP) ? "success" : "error"} />}
              variant="outlined"
              size="small"
              color={(product.BodyHTML && product.Title && product.MAP) ? "success" : "error"}
              title="View Shopify Integration Tab Below"
              label={(product.BodyHTML && product.Title && product.MAP) ? "Shopify Ready" : "Not Shopify Ready"}
            />
            <Chip
              icon={<MoveUpRight color={product.AutoUpdate ? "success" : "error"} />}
              variant="outlined"
              size="small"
              color={product.AutoUpdate ? "success" : "error"}
              title={product.AutoUpdate ? `Automatic Reorder (${product.MasterCarton})` : `Manual Reorder (${product.MasterCarton})`}
              label={product.AutoUpdate ? `Automatic Reorder (${product.MasterCarton})` : `Manual Reorder (${product.MasterCarton})`}
            />
          </div>

          <Typography component="h2" color="primary">Standard Cost</Typography>
          <p className="text-[32px] font-bold text-gray-500">
            <FormattedNumber value={product.Distribution?.Cost || 0} {...CurrencyUtils.format} />
          </p>
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-2">
          <div style={{ border: "1px solid #582c83", borderRadius: '20px', width: '100%', textAlign: "center" }}>
            <Table>
              <TableBody>
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                  <TableCell sx={{ borderColor: "#582c83", borderTopLeftRadius: '20px' }}>
                    <Typography><b>MPN</b></Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#582c83", borderTopRightRadius: '20px' }}>
                    <Typography>{product.MPN}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                  <TableCell sx={{ borderColor: "#582c83" }}>
                    <Typography><b>Manufacturer</b></Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#582c83" }}>
                    <Typography>{product.ManufacturerName}</Typography>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                  <TableCell sx={{ borderColor: "#582c83" }}>
                    <Typography><b>Category</b></Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#582c83" }}>
                    <Typography>
                      {product.ManufacturerCategory ?
                        product.IsManufacturer ?
                          <Link to={Path.category(applicationId!, product.ManufacturerCategory?.CategoryCode ?? undefined)}>
                            {product.ManufacturerCategory?.CategoryName}
                          </Link>
                          :
                          <>{product.ManufacturerCategory?.CategoryName}</>
                        :
                        <>{product.ProductType}</>
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
                {product.UPC &&
                  <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                    <TableCell sx={{ borderColor: "#582c83" }}>
                      <Typography><b>UPC</b></Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: "#582c83" }}>
                      <Typography>{product.UPC}</Typography>
                    </TableCell>
                  </TableRow>
                }
                {product.MAP &&
                  <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                    <TableCell sx={{ borderColor: "#582c83" }}>
                      <Typography title="Minimum Advertised Price"><b>MAP</b></Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: "#582c83" }}>
                      <FormattedNumber value={product.MAP} {...CurrencyUtils.format} />
                    </TableCell>
                  </TableRow>
                }
                {product.CreatedBy &&
                  <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                    <TableCell sx={{ borderColor: "#582c83" }}>
                      <Typography><b>Created by</b></Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: "#582c83" }}>
                      <Typography>{product.CreatedBy}</Typography>
                    </TableCell>
                  </TableRow>
                }
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }}>
                  <TableCell sx={{ border: "none", borderBottomLeftRadius: '20px' }}>
                    <Typography><b>Unit Weight</b></Typography>
                  </TableCell>
                  <TableCell sx={{ border: "none", borderBottomRightRadius: '20px' }}>
                    <Typography>{product.UnitWeight === 0 ? "Unset" : `${product.UnitWeight} lbs`}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductGeneral;
